import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Popover from 'material-ui/Popover';
import { withTranslation } from 'react-i18next';
import { SlateDialog } from 'components/Shared/SlateDialog';
import EmptyState from 'components/Shared/EmptyState/EmptyState';
import InspectRecordsHeaderCell from 'components/Inspect/InspectRecordsHeaderCell';
import {
  DeficiencyNoteDialog,
  ActivityLogDialog,
  VoidInspectionDialog,
  ACTION_COPY,
} from 'components/Inspect/InspectDeficiencyDialogs';
import { DEFICIENCY_ACTIONS, isSortableDescriptor } from 'store/actions/inspect';
import {
  formatDueDelta,
  formatZonedDate,
  formatZonedDateTime,
  isDueToday,
} from 'components/Inspect/scheduledDelta';
import { cadenceHeadline } from 'components/InspectSettings/sections/recurrence/describeRecurrence';
import {
  markerBadgeText,
  resultLabelText,
} from 'components/Inspect/resultLabels';
import layoutInspectColumns from 'components/Inspect/layoutInspectColumns';
import { renderFieldCell } from 'components/FieldColumns/fieldTypes';
import {
  formatColumnPath,
  formatColumnAttribute,
  formatColumnHeaderText,
} from 'components/FieldColumns/columnHeader';
import { FREQUENCIES } from 'helpers/recurrenceLimits';
import 'components/ScrollableTable/styles.css';
var moment = require('moment');

// Deficiency descriptors ride along on every History row (status, source) but
// are never table columns: the Result chip folds them in. 'summary' is
// dropped here too since the hardcoded SUMMARY_COLUMN below already renders
// that data. 'state' and 'amended' also ride along -- the Void marker/filter
// and the Amended marker read them straight off the row, same as
// deficiencyStatus/resultSource. 'activityLog' is gone: the API dropped that
// column (spec section 7) now that View Log reads the timeline endpoint
// instead.
var HIDDEN_DESCRIPTOR_KEYS = {
  deficiencyStatus: true,
  resultSource: true,
  summary: true,
  state: true,
  amended: true,
};

// Lowercased obj[key], or '' when either is missing.
function lower(obj, key) {
  return ((obj && obj[key]) || '').toLowerCase();
}

// The current row for slateUUID, or null if it is not (or no longer) in
// records. Used to look the Activity Log's "current result" dot color up
// fresh every render rather than off the state.logFor snapshot taken when
// View Log was clicked -- a reindex-refresh or other write while the dialog
// is open updates records, and the dot should follow that update.
function recordForSlateUUID(records, slateUUID) {
  if (!records || !slateUUID) return null;
  for (var i = 0; i < records.length; i++) {
    if (records[i] && records[i].slateUUID === slateUUID) return records[i];
  }
  return null;
}

// Coerces the `amended` ride-along (an amendment count) to a boolean: how
// many times this entry has been amended. normalizeHistoryRow writes every
// non-dateTime cell through String(), so a zero count can arrive as '0' --
// Number() first, so that truthy string is not mistaken for "amended".
function amendedCount(record) {
  var n = Number(record && record.amended);
  return isNaN(n) ? 0 : n;
}

export function resultChipInfo(record) {
  var state = lower(record, 'state');
  var amended = amendedCount(record) > 0;
  // Void is a lifecycle state, not a result: it takes over the chip
  // regardless of whatever __inspect_result still holds (void never clears
  // it). Void is final, so it carries no markers; Amended rides along on the
  // other shapes below as an extra marker, never a substitute.
  if (state === 'voided') {
    return { label: 'void', className: 'void', manual: false, amended: false };
  }
  var result = lower(record, 'result');
  if (!result) return null;
  var manual = lower(record, 'resultSource') === 'manual';
  if (result === 'fail' && lower(record, 'deficiencyStatus') === 'closed') {
    return { label: 'corrected', className: 'corrected', manual: manual, amended: amended };
  }
  return { label: result, className: result, manual: manual, amended: amended };
}

// The markers a History chip carries, in display order (Manual, then
// Amended) -- the same order the app's InspectionResultChip uses.
export function chipMarkers(chip) {
  var markers = [];
  if (chip && chip.manual) markers.push('manual');
  if (chip && chip.amended) markers.push('amended');
  return markers;
}

// Re-exported for existing importers (this file's own Result column below,
// tests) -- the definition now lives in resultLabels.js, which
// InspectDeficiencyDialogs.js also imports directly for its Activity Log
// status chips.
export { resultLabelText };

// Scheduled-tab Frequency cell, stamped per row by the API: '' for no recurrence,
// 'manual' for manual recurrence, or a compact JSON cadence descriptor for a
// scheduled rule ({ frequency, interval, daysOfWeek, daysOfMonth, startDate, timeZone }).
// Anything that is not one of those three shapes renders as none, including a
// descriptor whose frequency is not one cadenceHeadline can put into words.
export function frequencyCellInfo(raw) {
  if (raw === undefined || raw === null || raw === '') return { kind: 'none' };
  var text = String(raw);
  if (text === 'manual') return { kind: 'manual' };
  var parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { kind: 'none' };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { kind: 'none' };
  }
  if (FREQUENCIES.indexOf(parsed.frequency) === -1) {
    return { kind: 'none' };
  }
  return { kind: 'cadence', recurrence: parsed };
}

export function availableDeficiencyActions(record) {
  // Void is terminal (spec section 6): no deficiency actions on a voided
  // entry, matching the API's own refusal ("This inspection is void.").
  if (lower(record, 'state') === 'voided') return [];
  var result = lower(record, 'result');
  var manual = lower(record, 'resultSource') === 'manual';
  var actions = [];
  if ((result === 'pass' || result === 'incomplete') && !manual)
    actions.push(DEFICIENCY_ACTIONS.MARK_DEFICIENT);
  if (result === 'fail') actions.push(DEFICIENCY_ACTIONS.CLOSE_OUT);
  if (manual) actions.push(DEFICIENCY_ACTIONS.REVERT);
  return actions;
}

// Void applies to every non-voided entry. Every History row is an inspection
// entry, completed unless voided, whatever its state cell holds (a missing
// state, a legacy row, or a stored `draft` from dev data); the API applies
// the same rule.
export function canVoidRecord(record) {
  if (!record) return false;
  return lower(record, 'state') !== 'voided';
}

// label is replaced at render time with the "Summary by <SlateMate logo>"
// header (it needs instance click handlers for the info dialog); labelText is
// the plain-string form for places that cannot take a node, like the filter
// popover's input placeholder.
var SUMMARY_COLUMN = {
  key: 'summary',
  label: 'Summary',
  labelText: 'Summary',
  filterable: true,
  sortable: true,
  render: 'text',
};

// Zero-content filler appended when the Summary column is hidden (Scheduled
// tab) so data columns stay left-packed at their natural width instead of
// stretching proportionally across the table.
var SPACER_COLUMN = {
  key: 'spacer',
  label: '',
  filterable: false,
  sortable: false,
  render: 'spacer',
};

// Maps inspectionUUID -> recordsFieldTitle from the flat byInspection pivot so the
// aggregate "Inspection" column can display inspection names instead of raw UUIDs.
// byInspection items are already flat (one entry per inspection), so no tree walk.
function buildInspectionNameMap(inspectionsByInspection) {
  var map = {};
  if (!inspectionsByInspection) return map;
  for (var i = 0; i < inspectionsByInspection.length; i++) {
    var item = inspectionsByInspection[i];
    if (item && item.inspectionUUID) {
      map[item.inspectionUUID] = item.recordsFieldTitle || '';
    }
  }
  return map;
}

function parseTimestampToMoment(rawTs) {
  if (rawTs === null || rawTs === undefined || rawTs === '') return null;
  var tsNum = typeof rawTs === 'number' ? rawTs : Number(rawTs);
  if (!isNaN(tsNum)) {
    return tsNum < 1e12 ? moment.unix(tsNum) : moment(tsNum);
  }
  return moment(rawTs);
}

// True when a Scheduled row's own rule is day-coarse (Task 8/9). Manual rows
// and time-based rules carry granularity 'time' -- Manual has no time zone at
// all, so it must never take the zoned-date path below.
function isDayGranularity(record) {
  return !!record && record.granularity === 'day';
}

// A timed Scheduled row: the API reports its rule zone with it. A Manual row is
// also "time" granularity but carries no zone, and keeps the viewer-format path.
function hasRuleZoneTime(record) {
  return !!record && record.granularity === 'time' && !!record.timeZone;
}

// The rule's own IANA zone, or undefined when absent -- formatZonedDate and
// isDueToday both fall back to FALLBACK_TIME_ZONE (UTC) in that case rather
// than guessing the viewer's zone.
function rowTimeZone(record) {
  return record ? record.timeZone : undefined;
}

function deriveRenderMode(type) {
  if (type === 'dateTime') return 'datetime';
  if (type === 'selector') return 'badge';
  return 'text';
}

// Both the Summary column (History) and the spacer (Scheduled) soak up the
// table's leftover width via the fill-col class.
function isFillColumn(col) {
  return col.key === 'summary' || col.key === 'spacer';
}

// Maps the raw Scheduled `status` value from the API to a display chip.
// showDelta marks the actionable statuses whose chip also shows how far the
// due date is from now; Scheduled/Unknown chips stay plain.
var STATUS_BADGE_MAP = {
  overdue: { label: 'Overdue', className: 'overdue', showDelta: true },
  neglected: { label: 'Neglected', className: 'neglected', showDelta: true },
  'due-soon': { label: 'Due Soon', className: 'due-soon', showDelta: true },
  scheduled: { label: 'Scheduled', className: 'scheduled' },
  indeterminate: { label: 'Unknown', className: 'indeterminate' },
};

// The four pickable statuses for the Status column's multi-select filter.
// 'indeterminate' is a display-only fallback, not a filterable API value.
var STATUS_FILTER_OPTIONS = [
  'overdue',
  'neglected',
  'due-soon',
  'scheduled',
].map(function (value) {
  return {
    value: value,
    label: STATUS_BADGE_MAP[value].label,
    badgeClassName: STATUS_BADGE_MAP[value].className,
  };
});

// The pickable results for the History Result column's multi-select filter.
// Values are the raw lowercase API values (the same form the Performance KPI
// presets store); badge classes match the result cell chips. 'void' rewrites
// server-side to __inspect_state == voided (spec section 7's Void filter),
// not a result value -- picking it alongside another option still ORs
// correctly since the rewrite keys off the Result filter's raw values.
var RESULT_FILTER_OPTIONS = ['pass', 'fail', 'corrected', 'incomplete', 'void'].map(
  function (value) {
    return {
      value: value,
      label: value.charAt(0).toUpperCase() + value.slice(1),
      badgeClassName: value,
    };
  },
);

// Columns with a fixed value set get the multi-select option picker instead
// of the free-text filter popover. Add an entry here to make another column
// multi-select filterable.
var FILTER_OPTIONS_BY_COLUMN = {
  status: STATUS_FILTER_OPTIONS,
  result: RESULT_FILTER_OPTIONS,
};

// The 'passed' KPI includes closed (corrected) deficiencies, so callers
// filtering History to "passed" must match both results.
export var PASSED_RESULT_VALUES = ['pass', 'corrected'];

function getScheduledStatusInfo(rawStatus) {
  if (rawStatus === null || rawStatus === undefined || rawStatus === '')
    return null;
  var normalized = String(rawStatus).toLowerCase().replace(/_/g, '-');
  if (STATUS_BADGE_MAP.hasOwnProperty(normalized)) {
    return STATUS_BADGE_MAP[normalized];
  }
  // Unknown status values fall back to a neutral chip showing the raw label.
  return { label: String(rawStatus), className: 'indeterminate' };
}

var SCROLL_THRESHOLD_PX = 200;

// ---------------------------------------------------------------------------
// Loading skeleton
// Shimmer placeholders shown while records load, matching the Performance tab
// skeleton. Two contexts: a full table on cold load (no records yet) and a few
// appended rows on refresh / load-more (records already present).
// ---------------------------------------------------------------------------

var SKELETON_FILL_ROWS = 9;
var SKELETON_APPEND_ROWS = 3;

// Varied bar widths keyed by column index so text rows don't read as a rigid
// grid of identical blocks.
var SKELETON_TEXT_WIDTHS = ['58%', '72%', '46%', '64%', '52%'];

// Stand-in columns for the very first load, before the API returns the real
// column set, so the cold skeleton still reads as a full table.
var SKELETON_PLACEHOLDER_COLUMNS = [
  { key: 'sk-0', render: 'text' },
  { key: 'sk-1', render: 'badge' },
  { key: 'sk-2', render: 'text' },
  { key: 'sk-3', render: 'datetime' },
  { key: 'sk-4', render: 'text' },
];

function renderHeaderRow(columns, realHeaders, props, withActions) {
  // One path line per header row keeps titles aligned when any column has one.
  var showHierarchy = columns.some(function (c) {
    return !!c.path;
  });
  return (
    <tr>
      {columns.map(function (col) {
        var thClass = 'inspect-records-th';
        if (isFillColumn(col)) thClass += ' inspect-records-fill-col';
        var thContent = null;
        if (col.key !== 'spacer') {
          thContent = realHeaders ? (
            <InspectRecordsHeaderCell
              columnKey={col.key}
              columnType={col.type}
              label={col.label}
              labelText={col.labelText}
              path={col.path}
              attributeLabel={col.attributeLabel}
              title={col.title}
              showHierarchy={showHierarchy}
              filterable={col.filterable}
              sortable={col.sortable}
              filterValue={
                (props.columnFilters && props.columnFilters[col.key]) || ''
              }
              filterOptions={col.filterOptions}
              sortColumnKey={props.sortColumnKey}
              sortDirection={props.sortDirection}
              onFilterChange={props.onFilterChange}
              onSortChange={props.onSortChange}
            />
          ) : (
            <div className="inspect-skeleton-th-cell">
              <span className="inspect-skeleton-bar inspect-skeleton-bar-head" />
            </div>
          );
        }
        return (
          <th key={col.key} className={thClass}>
            {thContent}
          </th>
        );
      })}
      {withActions && (
        <th className="inspect-records-th inspect-records-actions-col" />
      )}
    </tr>
  );
}

function renderSkeletonBar(col, colIndex) {
  if (col.render === 'spacer') return null;
  var shape = 'text';
  if (col.render === 'datetime') shape = 'datetime';
  else if (col.render === 'badge' || col.key === 'status') shape = 'badge';
  else if (col.key === 'summary') shape = 'summary';

  var barStyle = null;
  if (shape === 'text') {
    barStyle = {
      width: SKELETON_TEXT_WIDTHS[colIndex % SKELETON_TEXT_WIDTHS.length],
    };
  }
  return (
    <span
      className={'inspect-skeleton-bar inspect-skeleton-bar-' + shape}
      style={barStyle}
    />
  );
}

function renderSkeletonRows(count, columns, keyPrefix) {
  var rows = [];
  for (var r = 0; r < count; r++) {
    rows.push(
      <tr
        key={keyPrefix + '-' + r}
        className="inspect-records-row inspect-skeleton-row"
        style={{ '--d': Math.min(r, 8) * 40 + 'ms' }}
        aria-hidden="true"
      >
        {columns.map(function (col, colIndex) {
          var tdClass = 'inspect-records-td inspect-skeleton-td';
          if (isFillColumn(col)) tdClass += ' inspect-records-fill-col';
          return (
            <td key={col.key} className={tdClass}>
              {renderSkeletonBar(col, colIndex)}
            </td>
          );
        })}
      </tr>,
    );
  }
  return rows;
}

export class InspectRecords extends Component {
  constructor(props) {
    super(props);
    this.scrollRef = React.createRef();
    this.scrollEl = null;
    this.state = {
      summaryInfoOpen: false,
      menu: null,
      noteOpen: false,
      note: null,
      logOpen: false,
      logFor: null,
      logEntries: [],
      logLoading: false,
      logError: null,
      voidOpen: false,
      voidFor: null,
    };
    this.handleScroll = this.handleScroll.bind(this);
    this.attachScrollListener = this.attachScrollListener.bind(this);
    this.detachScrollListener = this.detachScrollListener.bind(this);
    this.handleSummaryInfoOpen = this.handleSummaryInfoOpen.bind(this);
    this.handleSummaryInfoClose = this.handleSummaryInfoClose.bind(this);
    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.handleMenuActionClick = this.handleMenuActionClick.bind(this);
    this.handleViewLogClick = this.handleViewLogClick.bind(this);
    this.closeNoteDialog = this.closeNoteDialog.bind(this);
    this.closeLogDialog = this.closeLogDialog.bind(this);
    this.submitNote = this.submitNote.bind(this);
    this.handleVoidClick = this.handleVoidClick.bind(this);
    this.closeVoidDialog = this.closeVoidDialog.bind(this);
    this.loadVoidPreview = this.loadVoidPreview.bind(this);
    this.submitVoid = this.submitVoid.bind(this);
    // Bumped on every View Log click; the in-flight fetch's callbacks capture
    // the value at dispatch time and compare it back before touching state,
    // the same token-compare shape fetchInspectionRecords/fetchScheduledInspections
    // use in store/actions/inspect.js to drop a response that is no longer for
    // the current request (e.g. the user reopened View Log for a different
    // row, or closed the app, before the first request resolved).
    this._logRequestId = 0;
  }

  componentDidMount() {
    this.attachScrollListener();
  }

  componentDidUpdate() {
    this.attachScrollListener();
  }

  componentWillUnmount() {
    this.detachScrollListener();
    // Invalidate any in-flight View Log request so its callback's setState is
    // skipped after unmount.
    this._logRequestId += 1;
  }

  attachScrollListener() {
    var el = this.scrollRef.current;
    if (!el) {
      // The ref is gone (e.g. the skeleton branch renders a div without it) --
      // drop the stale listener so this.scrollEl does not dangle at a node React
      // has unmounted.
      this.detachScrollListener();
      return;
    }
    if (this.scrollEl === el) return;
    this.detachScrollListener();
    el.addEventListener('scroll', this.handleScroll);
    this.scrollEl = el;
  }

  detachScrollListener() {
    if (this.scrollEl) {
      this.scrollEl.removeEventListener('scroll', this.handleScroll);
      this.scrollEl = null;
    }
  }

  // stopPropagation keeps the info click from also triggering the header
  // title's sort toggle now that the summary column is sortable.
  handleSummaryInfoOpen(e) {
    e.stopPropagation();
    this.setState({ summaryInfoOpen: true });
  }

  handleSummaryInfoClose() {
    this.setState({ summaryInfoOpen: false });
  }

  openMenu(anchorEl, record) {
    this.setState({ menu: { record: record, anchorEl: anchorEl } });
  }

  closeMenu() {
    this.setState({ menu: null });
  }

  handleMenuActionClick(action) {
    this.setState({
      noteOpen: true,
      note: { record: this.state.menu.record, action: action },
      menu: null,
    });
  }

  // View Log now fetches the entry's timeline (spec section 7) instead of
  // reading the dropped activityLog column -- onViewActivity is threaded
  // down from Inspect.js, which resolves the inspectionUUID the same way
  // onDeficiencyAction does. The dialog opens immediately with its loading
  // state so the menu-close doesn't feel like a no-op while the request is
  // in flight.
  handleViewLogClick() {
    var record = this.state.menu.record;
    this.setState({
      logOpen: true,
      logFor: record,
      logEntries: [],
      logLoading: true,
      logError: null,
      menu: null,
    });
    this._logRequestId += 1;
    var requestId = this._logRequestId;
    if (typeof this.props.onViewActivity !== 'function') {
      this.setState({ logLoading: false });
      return;
    }
    var self = this;
    var t = this.props.t;
    this.props.onViewActivity(record).then(
      function (entries) {
        if (requestId !== self._logRequestId) return;
        self.setState({ logEntries: entries || [], logLoading: false });
      },
      function (err) {
        if (requestId !== self._logRequestId) return;
        self.setState({
          logLoading: false,
          logError: (err && err.message) ||
            t('inspect:activityLogLoadFailed', 'Could not load the activity log.'),
        });
      },
    );
  }

  // note (and logFor below) is intentionally kept after close so the
  // dialog's title/content do not flash to their empty-state fallback during
  // the fade-out transition.
  closeNoteDialog() {
    this.setState({ noteOpen: false });
  }

  closeLogDialog() {
    this.setState({ logOpen: false });
  }

  submitNote(noteText) {
    return this.props.onDeficiencyAction(
      this.state.note.record,
      this.state.note.action,
      noteText,
    );
  }

  handleVoidClick() {
    this.setState({
      voidOpen: true,
      voidFor: this.state.menu.record,
      menu: null,
    });
  }

  // voidFor is kept after close for the same fade-out reason as note/logFor.
  closeVoidDialog() {
    this.setState({ voidOpen: false });
  }

  loadVoidPreview() {
    if (typeof this.props.onVoidPreview !== 'function') {
      return Promise.resolve(null);
    }
    return this.props.onVoidPreview(this.state.voidFor);
  }

  submitVoid(reason) {
    return this.props.onVoidInspection(this.state.voidFor, reason);
  }

  // The logo pair reuses the SlateMate template builder's markup: both imgs
  // render and SlateGenerator/styles.css swaps them via [data-theme="dark"].
  renderSummaryHeaderLabel() {
    var t = this.props.t;
    return (
      <span className="inspect-summary-header-label">
        Summary by
        <img
          className="slatemate-logo slatemate-logo--light"
          src="/images/SlateMate-Logo.svg"
          alt="SlateMate"
        />
        <img
          className="slatemate-logo slatemate-logo--dark"
          src="/images/SlateMate-Logo-DarkMode.svg"
          alt="SlateMate"
        />
        <button
          type="button"
          className="inspect-summary-info-btn"
          aria-label={t('inspect:summaryColumnInfoAria')}
          onClick={this.handleSummaryInfoOpen}
        >
          <span className="material-symbols-outlined">info</span>
        </button>
      </span>
    );
  }

  handleScroll() {
    var el = this.scrollRef.current;
    if (!el) return;
    if (!this.props.hasMore) return;
    if (this.props.isLoadingMore) return;
    var distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < SCROLL_THRESHOLD_PX && this.props.loadMore) {
      this.props.loadMore();
    }
  }

  render() {
    var self = this;
    var props = this.props;
    var records = props.records;
    var isLoading = props.isLoading;
    var onRowClick = props.onRowClick;
    var slateInSlatePaneUUID = props.slateInSlatePaneUUID;
    var dateRange = props.dateRange;
    // The Activity Log dialog's newest dot is colored in the CURRENT result
    // of the row View Log was opened for -- the same resultChipInfo the
    // Result column itself renders -- so the dot matches that row's own
    // chip (spec: the dialog does not compute its own "current" result).
    // Looked up fresh in the current records by slateUUID every render
    // (falling back to the logFor snapshot if the row is no longer present)
    // so a reindex-refresh or other write while the dialog is open recolors
    // the dot instead of leaving it on the result the row had when View Log
    // was clicked. Deliberately NOT gated on logOpen too: logFor is kept set
    // through the dialog's ~225ms Material-UI close transition (see
    // closeLogDialog) so the newest dot does not flicker to neutral grey
    // while it fades out; resultChipInfo itself is cheap enough that
    // recomputing it on every render once a row's log has been viewed is
    // not worth that tradeoff.
    var logRecord = this.state.logFor
      ? recordForSlateUUID(props.records, this.state.logFor.slateUUID) ||
        this.state.logFor
      : null;
    var logChip = logRecord ? resultChipInfo(logRecord) : null;
    var logResultClassName = logChip ? logChip.className : null;

    // The "Inspection" column attributes each row to its inspection; it is only
    // meaningful in aggregate views ('all' / 'assetType'). Single-inspection views
    // ('inspection' / 'inspectionType') hide it since every row shares one inspection.
    var showInspectionColumn =
      props.selectionMode === 'all' || props.selectionMode === 'assetType';
    var sourceColumns =
      props.recordsColumns && props.recordsColumns.length > 0
        ? props.recordsColumns
        : [];
    var visibleKeys = sourceColumns
      .filter(function (descriptor) {
        // The API may return its own summary descriptor; drop it (via
        // HIDDEN_DESCRIPTOR_KEYS) since the hardcoded SUMMARY_COLUMN below
        // already renders that data (kept last, non-filterable, fill-width).
        if (HIDDEN_DESCRIPTOR_KEYS[descriptor.key]) return false;
        return showInspectionColumn || descriptor.key !== 'inspection';
      })
      .map(function (descriptor) {
        return descriptor.key;
      });
    var displayColumns = layoutInspectColumns(
      sourceColumns,
      props.customColumns,
      { visibleKeys: visibleKeys },
    ).map(function (descriptor) {
      if (descriptor.kind === 'custom') {
        // Display-only: custom columns are never sortable or filterable here.
        return {
          key: descriptor.key,
          kind: 'custom',
          label: descriptor.name,
          labelText: descriptor.name,
          type: descriptor.type,
          filterable: false,
          sortable: false,
          render: deriveRenderMode(descriptor.type),
          filterOptions: null,
          descriptor: descriptor,
          path: formatColumnPath(descriptor),
          attributeLabel: formatColumnAttribute(descriptor, props.t),
          title: formatColumnHeaderText(descriptor, props.t),
        };
      }
      var label =
        descriptor.key === 'timestamp'
          ? props.t('inspect:dateColumnLabel')
          : descriptor.name;
      return {
        key: descriptor.key,
        kind: 'builtin',
        label: label,
        type: descriptor.type,
        // Both affordances come from the descriptor: the API omits filterKey
        // and sets sortable false on a computed-phrase column (Frequency).
        filterable: !!descriptor.filterKey,
        sortable: isSortableDescriptor(descriptor),
        render: deriveRenderMode(descriptor.type),
        filterOptions: FILTER_OPTIONS_BY_COLUMN[descriptor.key] || null,
      };
    });
    // The Summary column only applies to History rows; Scheduled opts out via
    // showSummaryColumn={false} and gets the zero-content spacer instead so
    // both tabs keep data columns left-packed.
    if (props.showSummaryColumn !== false) {
      displayColumns = displayColumns.concat([
        Object.assign({}, SUMMARY_COLUMN, {
          label: this.renderSummaryHeaderLabel(),
        }),
      ]);
    } else {
      displayColumns = displayColumns.concat([SPACER_COLUMN]);
    }

    if (props.isReloading || (isLoading && !records)) {
      var hasRealColumns = sourceColumns.length > 0;
      var coldColumns = hasRealColumns
        ? displayColumns
        : SKELETON_PLACEHOLDER_COLUMNS;
      return (
        <div
          className="inspect-records inspect-records-skeleton"
          aria-busy="true"
        >
          <table className="inspect-records-table">
            <thead>{renderHeaderRow(coldColumns, hasRealColumns, props)}</thead>
            <tbody>
              {renderSkeletonRows(SKELETON_FILL_ROWS, coldColumns, 'cold')}
            </tbody>
          </table>
        </div>
      );
    }

    var hasActiveColumnFilters = false;
    if (props.columnFilters) {
      for (var fk in props.columnFilters) {
        if (
          props.columnFilters.hasOwnProperty(fk) &&
          props.columnFilters[fk] !== '' &&
          props.columnFilters[fk] !== null &&
          props.columnFilters[fk] !== undefined
        ) {
          hasActiveColumnFilters = true;
          break;
        }
      }
    }
    var hasActiveDateRange = !!(dateRange && (dateRange.from || dateRange.to));
    var filtersActive = hasActiveColumnFilters || hasActiveDateRange;

    // The universal date range is applied server-side (unix-second timestamp
    // bounds in the history query), so records arrive already scoped -- no
    // client-side date filtering here. hasActiveDateRange only drives which
    // empty state to show.
    var displayRecords = records || [];

    var inspectionNameMap = buildInspectionNameMap(
      props.inspectionsByInspection,
    );

    // The Scheduled status chip shows how far the due date is from now. The
    // due-date column is the row's dateTime descriptor (columns are API-driven,
    // so the key is not hardcoded).
    var dueDateKey = null;
    for (var ci = 0; ci < displayColumns.length; ci++) {
      // Built-in columns only: a custom dateTime column is never the due date.
      if (
        displayColumns[ci].kind === 'builtin' &&
        displayColumns[ci].render === 'datetime'
      ) {
        dueDateKey = displayColumns[ci].key;
        break;
      }
    }

    var isEmpty = displayRecords.length === 0;
    var hasRowActions = typeof props.onDeficiencyAction === 'function';
    var canVoid = typeof props.onVoidInspection === 'function';
    var showVoidAction =
      canVoid && !!this.state.menu && canVoidRecord(this.state.menu.record);
    var showFilteredEmpty = isEmpty && filtersActive;
    var showUnfilteredEmpty = isEmpty && !filtersActive;

    return (
      <div className="inspect-records" ref={this.scrollRef}>
        <table className="inspect-records-table">
          <thead>
            {renderHeaderRow(displayColumns, true, props, hasRowActions)}
          </thead>
          <tbody>
            {displayRecords.map(function (record, index) {
              var isActive = record.slateUUID === slateInSlatePaneUUID;
              var rowClass = 'inspect-records-row';
              if (index % 2 !== 0) rowClass += ' even';
              if (isActive) rowClass += ' active';
              return (
                <tr
                  // slateUUID repeats across rows (one slate can have several
                  // scheduled inspections or history records), and duplicate
                  // keys corrupt reconciliation when a refresh replaces the
                  // set. Rows are stateless, so index-suffixed keys are safe:
                  // full reloads replace every row and pagination only appends.
                  key={(record.slateUUID || 'row') + '-' + index}
                  className={rowClass}
                  onClick={function () {
                    onRowClick(record.slateUUID);
                  }}
                >
                  {displayColumns.map(function (col, colIndex) {
                    var rawVal = record[col.key];
                    var cellValue =
                      rawVal !== undefined && rawVal !== null
                        ? String(rawVal)
                        : '';
                    if (
                      col.key === 'inspection' &&
                      cellValue &&
                      inspectionNameMap[cellValue]
                    ) {
                      cellValue = inspectionNameMap[cellValue];
                    }
                    var cellContent;
                    if (col.kind === 'custom') {
                      var customValues = record.custom || {};
                      cellContent = renderFieldCell(
                        customValues[col.key],
                        col.descriptor,
                        { showEmptyImages: false },
                      );
                    } else if (col.key === 'status') {
                      var statusInfo = getScheduledStatusInfo(record[col.key]);
                      var dueMoment = dueDateKey
                        ? parseTimestampToMoment(record[dueDateKey])
                        : null;
                      // A day-coarse rule due today is due-soon, not overdue
                      // (Task 8) -- "Due Today" says so in words instead of a
                      // bare date + a "0 days" delta that would read as
                      // absurd. Same due-soon token, bucket, filter and
                      // count; this only changes the label.
                      var isDueTodayRow =
                        statusInfo === STATUS_BADGE_MAP['due-soon'] &&
                        isDayGranularity(record) &&
                        isDueToday(dueMoment, rowTimeZone(record));
                      var dueDelta = null;
                      if (
                        statusInfo &&
                        statusInfo.showDelta &&
                        dueDateKey &&
                        !isDueTodayRow
                      ) {
                        dueDelta = formatDueDelta(dueMoment);
                      }
                      var statusLabel = isDueTodayRow
                        ? props.t('inspect:scheduledDueToday')
                        : statusInfo && statusInfo.label;
                      cellContent = statusInfo ? (
                        <span
                          className={
                            'inspect-result-badge ' + statusInfo.className
                          }
                        >
                          {statusLabel}
                          {dueDelta && (
                            <span className="inspect-result-badge-divider" />
                          )}
                          {dueDelta && (
                            <span className="inspect-result-badge-delta">
                              {dueDelta}
                            </span>
                          )}
                        </span>
                      ) : (
                        ''
                      );
                    } else if (col.render === 'datetime') {
                      var tsMoment = parseTimestampToMoment(record[col.key]);
                      if (!tsMoment || !tsMoment.isValid()) {
                        cellContent = '';
                      } else if (
                        col.key === dueDateKey &&
                        isDayGranularity(record)
                      ) {
                        // B8: the Scheduled tab merges rows from many rules,
                        // each in its own zone -- render the day-coarse due
                        // date in that rule's zone, not the viewer's.
                        cellContent = formatZonedDate(
                          tsMoment,
                          rowTimeZone(record),
                        );
                      } else if (
                        col.key === dueDateKey &&
                        hasRuleZoneTime(record)
                      ) {
                        // S19: a timed due time is shown in the inspection's
                        // own zone, with its time, whatever the viewer's zone.
                        cellContent = formatZonedDateTime(
                          tsMoment,
                          rowTimeZone(record),
                        );
                      } else {
                        cellContent = tsMoment.format(
                          props.dateOnlyTimestamps && col.key === dueDateKey
                            ? 'MM/DD/YYYY'
                            : 'MM/DD/YYYY h:mm a',
                        );
                      }
                    } else if (col.key === 'summary' && cellValue) {
                      cellContent = (
                        <div className="inspect-summary-cell">{cellValue}</div>
                      );
                    } else if (col.key === 'result') {
                      var chip = resultChipInfo(record);
                      cellContent = chip ? (
                        <span className="inspect-result-badges">
                          <span
                            className={'inspect-result-badge ' + chip.className}
                          >
                            {resultLabelText(chip.label, props.t)}
                          </span>
                          {chipMarkers(chip).map(function (marker) {
                            return (
                              <span
                                key={marker}
                                className="inspect-result-badge marker"
                              >
                                {markerBadgeText(marker, props.t)}
                              </span>
                            );
                          })}
                        </span>
                      ) : (
                        ''
                      );
                    } else if (col.key === 'user') {
                      // Every History row is completed or voided, so a row with
                      // no completedByName was never given one -- that IS what
                      // "backfilled" means (spec section 2). A voided entry
                      // keeps whatever inspector line it had at completion, so
                      // this reads off the same emptiness, not the void
                      // itself. A stored dev `draft` row with no name also
                      // reads "unknown" (it predates the completion stamp).
                      cellContent =
                        cellValue ||
                        props.t(
                          'inspect:originalInspectorUnknown',
                          'Original inspector unknown',
                        );
                    } else if (col.key === 'frequency') {
                      var freq = frequencyCellInfo(record[col.key]);
                      if (freq.kind === 'manual') {
                        cellContent = (
                          <span className="inspect-result-badge upcoming">
                            {props.t('inspect:frequencyManual', 'Manual')}
                          </span>
                        );
                      } else if (freq.kind === 'cadence') {
                        try {
                          cellContent = cadenceHeadline(freq.recurrence, props.t);
                        } catch (e) {
                          cellContent = '-';
                        }
                      } else {
                        cellContent = '-';
                      }
                    } else if (col.render === 'badge' && cellValue) {
                      var badgeClass = 'inspect-result-badge';
                      if (cellValue.toLowerCase() === 'pass')
                        badgeClass += ' pass';
                      else if (cellValue.toLowerCase() === 'fail')
                        badgeClass += ' fail';
                      else if (cellValue.toLowerCase() === 'incomplete')
                        badgeClass += ' incomplete';
                      cellContent = (
                        <span className={badgeClass}>{cellValue}</span>
                      );
                    } else {
                      cellContent = cellValue;
                    }
                    var tdClass = 'inspect-records-td';
                    if (isFillColumn(col))
                      tdClass += ' inspect-records-fill-col';
                    return (
                      <td key={col.key} className={tdClass}>
                        {cellContent}
                      </td>
                    );
                  })}
                  {hasRowActions && (
                    <td className="inspect-records-td inspect-records-actions-col">
                      <button
                        type="button"
                        className={
                          'inspect-row-menu-btn' +
                          (self.state.menu && self.state.menu.record === record
                            ? ' open'
                            : '')
                        }
                        aria-label={props.t(
                          'inspect:rowActionsAria',
                          'Record actions',
                        )}
                        onClick={function (e) {
                          e.stopPropagation();
                          self.openMenu(e.currentTarget, record);
                        }}
                      >
                        <span className="material-symbols-outlined">
                          more_vert
                        </span>
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {props.isLoadingMore &&
              renderSkeletonRows(SKELETON_APPEND_ROWS, displayColumns, 'more')}
          </tbody>
        </table>
        {showFilteredEmpty && (
          <div className="inspect-records-filtered-empty">
            <EmptyState
              icon="filter_alt_off"
              title={props.t('inspect:noMatchingRecordsTitle')}
              description={props.t('inspect:noMatchingRecordsDescription')}
            />
          </div>
        )}
        {showUnfilteredEmpty && (
          <div className="inspect-records-filtered-empty">
            <EmptyState
              icon="fact_check"
              title={props.emptyTitle}
              description={props.emptyDescription}
              buttonLabel={props.emptyActionLabel}
              onAction={props.onCreateInspection}
            />
          </div>
        )}
        <SlateDialog
          open={this.state.summaryInfoOpen}
          onClose={this.handleSummaryInfoClose}
          title={props.t('inspect:summaryColumnInfoTitle')}
          maxWidth="xs"
        >
          <div className="inspect-summary-info-body">
            {props.t('inspect:summaryColumnInfoBody')}
          </div>
        </SlateDialog>
        <Popover
          open={!!this.state.menu}
          anchorEl={this.state.menu ? this.state.menu.anchorEl : null}
          onRequestClose={this.closeMenu}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          targetOrigin={{ horizontal: 'right', vertical: 'top' }}
          className="inspect-row-menu-popover"
          zDepth={0}
          style={{
            // Longhand on purpose: Paper sets backgroundColor, and Safari keeps
            // it over a `background` shorthand, leaving white corners.
            backgroundColor: 'transparent',
            borderRadius: 10,
            boxShadow: 'none',
            overflow: 'visible',
          }}
        >
          <div className="inspect-row-menu">
            <button
              type="button"
              className="inspect-row-menu-item"
              onClick={this.handleViewLogClick}
            >
              <span className="material-symbols-outlined inspect-row-menu-item-icon">
                history
              </span>
              <span className="inspect-row-menu-item-label">
                {props.t('inspect:viewLog', 'View Log')}
              </span>
            </button>
            {this.state.menu &&
              (availableDeficiencyActions(this.state.menu.record).length >
                0 ||
                showVoidAction) && <div className="inspect-row-menu-divider" />}
            {this.state.menu &&
              availableDeficiencyActions(this.state.menu.record).map(
                function (action) {
                  var copy = ACTION_COPY[action];
                  return (
                    <button
                      key={action}
                      type="button"
                      className="inspect-row-menu-item"
                      onClick={function () {
                        self.handleMenuActionClick(action);
                      }}
                    >
                      <span className="material-symbols-outlined inspect-row-menu-item-icon">
                        {copy.icon}
                      </span>
                      <span className="inspect-row-menu-item-label">
                        {props.t(copy.title, copy.titleFallback)}
                      </span>
                    </button>
                  );
                },
              )}
            {showVoidAction && (
              <button
                type="button"
                className="inspect-row-menu-item"
                onClick={this.handleVoidClick}
              >
                <span className="material-symbols-outlined inspect-row-menu-item-icon">
                  block
                </span>
                <span className="inspect-row-menu-item-label">
                  {props.t('inspect:voidInspection', 'Void Inspection')}
                </span>
              </button>
            )}
          </div>
        </Popover>
        <DeficiencyNoteDialog
          open={this.state.noteOpen}
          action={this.state.note ? this.state.note.action : null}
          onClose={this.closeNoteDialog}
          onSubmit={this.submitNote}
          t={props.t}
        />
        <ActivityLogDialog
          open={this.state.logOpen}
          onClose={this.closeLogDialog}
          entries={this.state.logEntries}
          loading={this.state.logLoading}
          error={this.state.logError}
          currentResultClassName={logResultClassName}
          imagesLinks={props.imagesLinks}
          t={props.t}
        />
        {canVoid && (
          <VoidInspectionDialog
            open={this.state.voidOpen}
            onClose={this.closeVoidDialog}
            onPreview={this.loadVoidPreview}
            onSubmit={this.submitVoid}
            t={props.t}
          />
        )}
      </div>
    );
  }
}

InspectRecords.propTypes = {
  records: PropTypes.array,
  recordsColumns: PropTypes.array,
  customColumns: PropTypes.array,
  isLoading: PropTypes.bool,
  onRowClick: PropTypes.func.isRequired,
  slateInSlatePaneUUID: PropTypes.string,
  dateRange: PropTypes.object,
  emptyTitle: PropTypes.string,
  emptyDescription: PropTypes.string,
  emptyActionLabel: PropTypes.string,
  onCreateInspection: PropTypes.func,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  isReloading: PropTypes.bool,
  loadMore: PropTypes.func,
  columnFilters: PropTypes.object,
  sortColumnKey: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc', null]),
  onFilterChange: PropTypes.func,
  onSortChange: PropTypes.func,
  selectionMode: PropTypes.string,
  inspectionsByInspection: PropTypes.array,
  showSummaryColumn: PropTypes.bool,
  onDeficiencyAction: PropTypes.func,
  onViewActivity: PropTypes.func,
  onVoidPreview: PropTypes.func,
  onVoidInspection: PropTypes.func,
  imagesLinks: PropTypes.object,
};

export default withTranslation()(InspectRecords);
